import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Row, Table } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Loading from "../components/Loading";
import { authenticatedFetch } from "../helpFunctions/authFetch";
import { getEnvVariable } from "../helpFunctions/getEnvVariable";

const PAGE_SIZE = 5;

const apiUrl = (path) => `${getEnvVariable("API_BASE_URL")}preference/surveys${path}`;

const errorText = async (response) => {
  try {
    const body = await response.json();
    if (body.detail?.errors) return body.detail.errors.join("; ");
    return typeof body.detail === "string" ? body.detail : `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
};

const AppPoolBadge = ({ enabled }) =>
  enabled ? <Badge bg="success">In app pool</Badge> : <Badge bg="secondary">Not in app pool</Badge>;

export const PreferenceSurveyList = () => {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    authenticatedFetch(navigate, apiUrl("")).then(async (response) => {
      if (response.ok) setSurveys((await response.json()).surveys);
      else setError(await errorText(response));
    });
  }, [navigate]);

  if (error) return <Container className="my-4"><Alert variant="warning">{error}</Alert></Container>;
  if (!surveys) return <Loading message="Loading preference surveys" />;

  return (
    <Container className="my-4" style={{ maxWidth: "1400px" }}>
      <h1 className="mb-4">Preference surveys</h1>
      {surveys.length === 0 ? (
        <p>No preference surveys yet. Create one through the API from a manifest.</p>
      ) : (
        <Table hover responsive>
          <thead>
            <tr>
              <th>Name</th><th>Media</th><th>Prompts</th><th>Pairs</th><th>Completed</th><th>Votes</th><th>App</th><th />
            </tr>
          </thead>
          <tbody>
            {surveys.map((s) => (
              <tr key={s.preference_survey_id}>
                <td>{s.name}</td>
                <td>{s.media_type}</td>
                <td>{s.group_count}</td>
                <td>{s.pair_count}</td>
                <td>{s.completed_pair_count}</td>
                <td>{s.vote_count}</td>
                <td><AppPoolBadge enabled={s.app_enabled} /></td>
                <td><Link to={`/preference/${s.preference_survey_id}`}>Preview</Link></td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

const Media = ({ type, url }) => {
  if (type === "image") return <img src={url} alt="" className="w-100 rounded" />;
  if (type === "audio") return <audio src={url} controls preload="metadata" className="w-100" />;
  return <video src={url} controls muted loop playsInline preload="metadata" className="w-100 rounded" />;
};

const formatMeta = (meta) =>
  meta ? Object.entries(meta).map(([key, value]) => `${key}: ${JSON.stringify(value)}`).join(", ") : "";

export const PreferenceSurveyPreview = () => {
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setPage(null);
    authenticatedFetch(navigate, apiUrl(`/${surveyId}/preview?offset=${offset}&limit=${PAGE_SIZE}`))
      .then(async (response) => {
        if (response.ok) setPage(await response.json());
        else setError(await errorText(response));
      });
  }, [navigate, surveyId, offset]);

  useEffect(load, [load]);

  const setAppPool = async (enabled) => {
    const action = enabled ? "show this survey to everyone using the app" : "stop serving this survey in the app";
    if (!window.confirm(`This will ${action}. Continue?`)) return;
    setSaving(true);
    const response = await authenticatedFetch(navigate, apiUrl(`/${surveyId}`), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ app_enabled: enabled }),
    });
    setSaving(false);
    if (response.ok) {
      const survey = await response.json();
      setPage((current) => ({ ...current, survey }));
    } else {
      setError(await errorText(response));
    }
  };

  if (error) return <Container className="my-4"><Alert variant="warning">{error}</Alert></Container>;
  if (!page) return <Loading message="Loading preview" />;

  const { survey, groups, total_groups: total } = page;
  const lastShown = Math.min(offset + groups.length, total);

  return (
    <Container className="my-4" style={{ maxWidth: "1400px" }}>
      <Link to="/preference">All preference surveys</Link>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-start mt-2 mb-4 gap-3">
        <div>
          <h1 className="mb-1">{survey.name}</h1>
          <div className="text-muted mb-2">“{survey.question}”</div>
          <div className="small text-muted">
            {survey.media_type} · {survey.group_count} prompts · {survey.pair_count} pairs ·{" "}
            {survey.completed_pair_count} completed · {survey.vote_count} votes · {survey.default_target_votes} votes per pair
          </div>
          <div className="small text-muted text-break">{survey.manifest_uri}</div>
        </div>
        <div className="text-md-end">
          <div className="mb-2"><AppPoolBadge enabled={survey.app_enabled} /></div>
          {page.can_manage_app_pool && (
            <Button
              variant={survey.app_enabled ? "outline-danger" : "primary"}
              disabled={saving}
              onClick={() => setAppPool(!survey.app_enabled)}
            >
              {survey.app_enabled ? "Remove from app pool" : "Add to app pool"}
            </Button>
          )}
        </div>
      </div>

      {groups.map((group) => (
        <Card key={group.group} className="mb-4">
          <Card.Header>
            <strong>{group.group}</strong>
            {group.prompt ? <span className="ms-2">{group.prompt}</span> : <em className="ms-2 text-muted">no prompt</em>}
          </Card.Header>
          <Card.Body>
            <Row xs={1} sm={2} lg={4} className="g-3">
              {group.candidates.map((candidate) => (
                <Col key={candidate.id}>
                  <Media type={survey.media_type} url={candidate.url} />
                  <div className="small mt-1 text-break">{candidate.id}</div>
                  <div className="small text-muted">{formatMeta(candidate.meta)}</div>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      ))}

      <div className="d-flex justify-content-between align-items-center">
        <Button variant="outline-secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
          Previous
        </Button>
        <span className="text-muted">Prompts {total ? offset + 1 : 0}–{lastShown} of {total}</span>
        <Button variant="outline-secondary" disabled={lastShown >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
          Next
        </Button>
      </div>
    </Container>
  );
};
